// Fetch wrapper — auto-attaches Bearer token, surfaces 401 by clearing
// the session and dispatching a 'ngb:unauthorized' event so the router
// can redirect to /login.

import { clearSession, getToken } from './auth';

export class ApiError extends Error {
  status: number;
  detail: unknown;
  constructor(status: number, message: string, detail?: unknown) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

export async function api<T = unknown>(
  path: string,
  init: RequestInit = {}
): Promise<T> {
  const headers = new Headers(init.headers || {});
  if (!headers.has('Content-Type') && init.body) {
    headers.set('Content-Type', 'application/json');
  }
  const token = getToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const r = await fetch(path, { ...init, headers });

  if (r.status === 401 && path !== '/api/auth/login') {
    clearSession();
    window.dispatchEvent(new CustomEvent('ngb:unauthorized'));
    throw new ApiError(401, 'Unauthorized');
  }

  let body: any = null;
  try {
    body = await r.json();
  } catch {
    body = null;
  }

  if (!r.ok) {
    throw new ApiError(r.status, body?.error || `HTTP ${r.status}`, body);
  }
  return body as T;
}

// Typed helpers
export const apiGet = <T>(path: string) => api<T>(path);
export const apiPost = <T>(path: string, body: unknown) =>
  api<T>(path, { method: 'POST', body: JSON.stringify(body) });
export const apiPatch = <T>(path: string, body: unknown) =>
  api<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
export const apiDelete = <T>(path: string) => api<T>(path, { method: 'DELETE' });
export const apiPut = <T>(path: string, body: unknown) =>
  api<T>(path, { method: 'PUT', body: JSON.stringify(body) });
